import React, { useState, useEffect, useContext } from 'react';
import { ChatContextProvider, ChatContext } from './context/ChatContext';
import ChatDashboard from './components/ChatDashboard';
import LoadingSpinner from './components/LoadingSpinner';
import socket from './services/socket';
import AsyncLocalStorage from '@createnextapp/async-local-storage';
import { v4 as uuidv4 } from "uuid";
import { useAuth } from '../../../context/AuthContext';
 

const ChatWrapper = () => { 
  return (
    <ChatContextProvider>
      <ChatMain />
    </ChatContextProvider>
  );
};

const ChatMain = () => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const { dispatch, data } = useContext(ChatContext);
  const currentChatRef = React.useRef(data.currentChat);
  const lastMessagesRef = React.useRef(data.lastMessages);
  const { user } = useAuth(); // Get user from auth context

 
  useEffect(() => {       
    // keep refs up to date so socket handlers can read latest values
    currentChatRef.current = data.currentChat;
    lastMessagesRef.current = data.lastMessages;
    
    // Only initialize if we have a user and haven't initialized yet
    if (user && user.user_id && !data.user?.id) {
      initializeConnection();
    }

    return () => {
      // Cleanup socket listeners
      socket.off("last-messages");
      socket.off("connect_error");
      socket.off("active-chats");  
      socket.off("connect");
      socket.off("reconnect_attempt");
      socket.off("channel-messages");
      socket.off("receive-message");
    };
  }, [user?.user_id]); // Depend on user_id to prevent duplicate calls

  const initializeConnection = async () => {
    try { 
      setIsLoading(true);
      setError(null);

      // Check if user is available
      if (!user || !user.user_id || !user.user_full_name) {
        setError("User session not available. Please log in again.");
        setIsLoading(false);
        return;
      }

      // Get or create client ID
      let clientId = await AsyncLocalStorage.getItem("silu-ClientId");
      if (!clientId) {
        clientId = uuidv4();
        await AsyncLocalStorage.setItem("silu-ClientId", clientId); 
      } else {
        console.log('Using existing clientId:', clientId);
      }

      // Set user in context (equivalent to original web app's SET_USER)
      const firstName = user.user_full_name.split(' ')[0];
      dispatch({ type: "SET_USER", payload: {"id": user.user_id, "name": firstName } });

      // Set up socket listeners (same as original web app)
      socket.off("last-messages").on("last-messages", (messages) => {
        console.log('🔍 SOCKET DEBUG - Received last-messages:', {
          count: messages?.length || 0,
          sample: messages?.slice(0, 3)?.map(m => ({
            date: new Date((m.timestamp || 0) * 1000).toISOString(),
            name: m.name,
            channel: m.channel_id
          }))
        });
        
        if (messages && messages.length > 0) {
          // Group by date for debugging
          const byDate = {};
          messages.forEach(chat => {
            const date = new Date((chat.timestamp || 0) * 1000).toISOString().split('T')[0];
            if (!byDate[date]) byDate[date] = [];
            byDate[date].push(chat);
          });
          console.log('🔍 SOCKET DEBUG - Messages by date:', Object.keys(byDate).sort().reverse().map(date => `${date}: ${byDate[date].length}`));
        }
        
        dispatch({ type: "SET_LAST_MESSAGES", payload: messages });  
        setIsLoading(false);
      });

      socket.off("active-chats").on("active-chats", (chats) => {      
        dispatch({ type: "SET_ACTIVE_CHATS", payload: chats });  
        setIsLoading(false);
      });

      // Register receive-message listener at top level (like desktop app)
      socket.off("receive-message").on("receive-message", (message) => {
        console.log("📨 CRM Chat: Received message at top level:", message);

        // Get message channel
  const messageChannel = String(message.channel_id || message.channelId || '');
  const currentChannelId = String((currentChatRef.current && currentChatRef.current.channel_id) || '');

        // Determine unread count using latest lastMessages to avoid stale increments
        let unreadCount = 0;
        if (message.direction === 0) {
          // Incoming message
          if (messageChannel !== currentChannelId) {
            // Not the current chat - increment unread based on latest lastMessages
            const existingChat = (lastMessagesRef.current || []).find(
              chat => String(chat.channel_id) === messageChannel
            );
            unreadCount = (existingChat?.unread || 0) + 1;
          } else {
            // Current chat - mark as read
            unreadCount = 0;
          }
        } else {
          // Outgoing message - always 0
          unreadCount = 0;
        }

        // Update the chat list with the new message and unread count
        dispatch({
          type: "UPDATE_LAST_MESSAGE",
          payload: {
            channel_id: messageChannel,
            name: message.userName || message.name || '',
            message: message.message,
            timestamp: message.timestamp,
            unread: unreadCount
          }
        });

        // Also dispatch to context so MessagesDashboard can handle it for the open chat
        dispatch({ type: "RECEIVE_MESSAGE", payload: message });
      });

      socket.off("connect_error").on("connect_error", (err) => { 
        setError(`Connection error: ${err.message}. Attempting to reconnect...`); 
        setIsLoading(true);  
      });

      socket.off("reconnect_attempt").on("reconnect_attempt", (attemptNumber) => {
        console.log(`Socket reconnect attempt #${attemptNumber}`);
      });

      const performJoin = () => {
        const channelToJoin = data.currentChat?.channel_id;
        const userName = user.user_full_name.split(' ')[0]; // Use first name only like the original
        const userId = user.user_id;
 
        
        // Use exact same format as original - always include channel parameter
        socket.emit("join", { 
          clientId: clientId, 
          userName: userName, 
          department: 1, 
          direction: 1, 
          channel: channelToJoin || 0, // Send 0 instead of undefined/null
          client: 'agent' 
        });
         
      };

      if (socket.connected) {
        console.log("Socket already connected. Performing join.");
        performJoin();
      } else { 
        socket.off("connect").on("connect", () => { 
          console.log("Socket connected via listener. Performing join.");
          setError(null); 
          performJoin();
        });
        socket.connect();
      }

      // Set a timeout to stop loading even if no data comes back
      setTimeout(() => {
        console.log('Timeout reached, stopping loading');
        setIsLoading(false);
      }, 10000); // Increased timeout to 10 seconds

    } catch (err) {
      console.error("Error initializing chat:", err);
      setError("Failed to initialize chat. Please try again.");
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setIsRetrying(true);
    setTimeout(() => {
      setIsRetrying(false);
      initializeConnection();
    }, 1000);
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div>
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Connecting to chat server...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <button 
            onClick={handleRetry}
            disabled={isRetrying}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isRetrying ? 'Retrying...' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {console.log('ChatMain rendering ChatDashboard')}
      <ChatDashboard />
    </div>
  );
};

export default ChatWrapper;
